import { createContext, Fragment, type ReactNode, useContext, useId, useMemo } from 'react'

import glossaryJson from '../demo/fixtures/glossary.json'
import styles from './Glossary.module.css'

export interface GlossaryTerm { key: string; label: string; hover: string; paren: string }

interface GlossaryFixture { maxParenPerScreen: number; terms: GlossaryTerm[] }
interface GlossaryLedger {
  beginPass: (scopeKey: string) => void
  claim: (occurrenceId: string, termKey: string, requested: boolean) => boolean
}

const fixture = glossaryJson as unknown as GlossaryFixture
const GlossaryContext = createContext<GlossaryLedger | null>(null)

// Explicit public constants are part of the Dev Spec API; keeping them beside the rendering contract avoids a forbidden extra file.
// eslint-disable-next-line react-refresh/only-export-components
export const GLOSSARY_TERMS = fixture.terms
// eslint-disable-next-line react-refresh/only-export-components
export const GLOSSARY_MAX_PAREN = fixture.maxParenPerScreen

function createLedger(scopeKey: string): GlossaryLedger {
  let passKey = `provider:${scopeKey}`
  let count = 0
  const decisions = new Map<string, boolean>()
  const ownerByTerm = new Map<string, string>()
  return {
    beginPass: (scopeKey) => {
      if (passKey === scopeKey) return
      passKey = scopeKey
      count = 0
      decisions.clear()
      ownerByTerm.clear()
    },
    claim: (occurrenceId, termKey, requested) => {
      const existing = decisions.get(occurrenceId)
      if (existing !== undefined) return existing
      const approved = requested && !ownerByTerm.has(termKey) && count < GLOSSARY_MAX_PAREN
      decisions.set(occurrenceId, approved)
      if (approved) {
        ownerByTerm.set(termKey, occurrenceId)
        count += 1
      }
      return approved
    },
  }
}

export function GlossaryProvider({ scopeKey, children }: { scopeKey: string; children: ReactNode }) {
  const ledger = useMemo(() => createLedger(scopeKey), [scopeKey])
  return <GlossaryContext value={ledger}>{children}</GlossaryContext>
}

export function GlossaryRenderPass({ scopeKey, children }: { scopeKey: string; children: ReactNode }) {
  const ledger = useContext(GlossaryContext)
  ledger?.beginPass(scopeKey)
  return children
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGlossaryTerm(key: string): GlossaryTerm | undefined {
  return GLOSSARY_TERMS.find((term) => term.key === key)
}

export function Term({ k, paren = false }: { k: string; paren?: boolean }) {
  const occurrenceId = useId()
  const term = useGlossaryTerm(k)
  const ledger = useContext(GlossaryContext)
  if (!term) return <>{k}</>
  const approved = ledger?.claim(occurrenceId, term.key, paren) ?? false
  return (
    <>
      <span className={styles.gloss} title={term.hover}>{term.label}</span>
      {approved ? <span className={styles.paren}>（{term.paren}）</span> : null}
    </>
  )
}

const termsByLength = [...GLOSSARY_TERMS].sort((left, right) => right.label.length - left.label.length)

export function GlossaryText({ text, paren = false }: { text: string; paren?: boolean }) {
  const parts: ReactNode[] = []
  let plainStart = 0
  let cursor = 0
  while (cursor < text.length) {
    const term = termsByLength.find((candidate) => text.startsWith(candidate.label, cursor))
    if (!term) {
      cursor += 1
      continue
    }
    if (plainStart < cursor) parts.push(text.slice(plainStart, cursor))
    parts.push(<Term key={`${cursor}-${term.key}`} k={term.key} paren={paren} />)
    cursor += term.label.length
    plainStart = cursor
  }
  if (plainStart < text.length) parts.push(text.slice(plainStart))
  return <>{parts.map((part, index) => <Fragment key={index}>{part}</Fragment>)}</>
}
